import React, { useEffect, useRef, useState } from 'react'
import Editor from 'components/editor'
import Field from 'components/field'
import { Plus } from 'components/icons'
import { MenuItem, MenuList } from 'components/menu/items'
import { isEditorEmpty } from 'components/spreadsheet/helpers/functions'
import { EditorContent } from 'types'

interface ColumnDescriptionFieldProps {
  description: EditorContent | null | undefined
  editorId: string
  tablePublicId: string
  onChange: (content: EditorContent) => void
}

/**
 * Short rich text at 13px on 20px lines, as a field's text is: a list set in by its bullet's width (20px) and
 * its items on consecutive lines, 4px from the text around it, rather than the document's roomy spacing.
 */
export const COMPACT_RICH_TEXT = [
  '[&_.editor]:!leading-5 [&_.public-DraftEditor-content]:text-[13px] [&_.public-DraftEditor-content]:!leading-5',
  '[&_ul]:!my-1 [&_ul]:!ml-0 [&_ol]:!my-1 [&_ol]:!ml-0 [&_li]:!my-0',
  '[&_li.public-DraftStyleDefault-depth0]:!ml-5 [&_li.public-DraftStyleDefault-depth1]:!ml-10',
  '[&_li.public-DraftStyleDefault-depth2]:!ml-[60px]'
].join(' ')

/** A rich-text editor framed as a text field: the field fill, quiet border and blue focus ring. */
export const DESCRIPTION_FIELD = [
  'rounded-md border border-divider bg-field text-[13px] leading-5 text-body',
  'transition-[border-color,box-shadow] duration-150 hover:border-faint',
  'focus-within:border-blue focus-within:ring-3 focus-within:ring-blue/15',
  COMPACT_RICH_TEXT,
  '[&_.public-DraftEditorPlaceholder-root]:text-muted [&_.public-DraftEditorPlaceholder-root]:text-[13px]',
  // Room for three lines before it grows, so typing never moves what's under it until it must.
  '[&_.public-DraftEditor-content]:min-h-[60px]'
].join(' ')

// A column's description, shared by the add- and edit-column panels. Most columns don't have one, and a
// rich-text editor is tall in a panel this narrow, so an empty description sits behind an "Add
// description" link until asked for. Once shown it stays shown — clearing the text mid-edit mustn't
// whisk the editor away — so callers key it by column to start each column afresh.
const ColumnDescriptionField: React.FC<ColumnDescriptionFieldProps> = ({
  description,
  editorId,
  tablePublicId,
  onChange
}) => {
  const [open, setOpen] = useState(() => !!description && !isEditorEmpty(description))
  const focusOnOpen = useRef(false)
  const editorRef = useRef<HTMLDivElement>(null)

  // Revealing the editor is a request to type in it, so put the caret there rather than make the user
  // click a second time. Only after the link is clicked — an existing description mustn't steal focus.
  useEffect(() => {
    if (!open || !focusOnOpen.current) return
    focusOnOpen.current = false
    const focusEditor = () => editorRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus()
    focusEditor()
    // The editor can take a moment to become editable after it mounts; try once more when it has.
    const retry = window.setTimeout(focusEditor, 50)
    return () => window.clearTimeout(retry)
  }, [open])

  if (!open) {
    // A row, as a menu's are, for a setting most columns go without. Nothing under it, so the "Add default
    // value" row that follows in Edit column sits right against it, the two as the one list.
    return (
      <MenuList className="pb-0">
        <MenuItem
          icon={<Plus />}
          data-testid="add-column-description"
          onClick={() => {
            focusOnOpen.current = true
            setOpen(true)
          }}
        >
          Add description
        </MenuItem>
      </MenuList>
    )
  }

  return (
    // 4px under the editor, which a row that follows has none of above it.
    <Field label="Description" className="flex flex-col gap-1.5 px-3 pt-3 pb-1">
      {/* Set as the Name field above it is: a white field at 13px, 12px in, with the inputs' hover border and
          focus ring. Three lines tall, then growing a line at a time, with a + under the text for media; the
          formatting shows over text as it's selected. */}
      <div ref={editorRef} className={DESCRIPTION_FIELD}>
        <Editor
          databaseDoc={description}
          readOnly={false}
          editorId={editorId}
          border
          className="!rounded-md !border-0 !px-3 !pt-[5px] !pb-1"
          placeholder="Describe what this column holds."
          compactToolbar
          onChange={onChange}
          resources={[{ resource: 'document_table', publicId: tablePublicId }]}
        />
      </div>
    </Field>
  )
}

export default ColumnDescriptionField
